import { useEffect, useState } from 'react'
import { format } from 'date-fns'
import { Loader2 } from 'lucide-react'
import { dailyReportAPI } from '../services/api'
import { useAuthStore } from '../stores/authStore'
import { Card, CardHeader, CardTitle, CardContent, Button } from './ui'
import { formatMoney } from './DailyReportTable'
import AttributionModelDetails from './AttributionModelDetails'
import AttributionCreditTable from './AttributionCreditTable'
import AttributionChannelTable from './AttributionChannelTable'
import AttributionWindowControl, { AttributionWindowExplanation } from './AttributionWindowControl'
import { missingDaysNote } from '../lib/spendCoverage'
import { DEFAULT_LOOKBACK } from '../lib/attributionLookback'


const count = value => value.toLocaleString('en-US', { maximumFractionDigits: 2 })

export default function AttributionModels({ dateRange, refresh, view = 'comparison' }) {
  const { currentClinicId } = useAuthStore()
  const [modelId, setModelId] = useState('last-non-direct')
  const [scope, setScope] = useState('new')
  const lookbackDays = DEFAULT_LOOKBACK // all time for every brand; not a choice on the page
  const [attributionWindow, setAttributionWindow] = useState('all')
  const [accountingMode, setAccountingMode] = useState('payment')
  const [metric, setMetric] = useState('revenue')
  const [result, setResult] = useState(null)
  const [error, setError] = useState(null)
  const [retry, setRetry] = useState(0)
  const supported = Boolean(currentClinicId)
  const startDate = format(dateRange.from, 'yyyy-MM-dd')
  const endDate = format(dateRange.to, 'yyyy-MM-dd')

  useEffect(() => {
    let active = true
    setResult(null)
    setError(null)
    if (supported) {
      dailyReportAPI.getAttributionModels({ startDate, endDate, scope, lookbackDays, attributionWindow, accountingMode })
        .then(response => { if (active) setResult(response.data.data) })
        .catch(err => { if (active) setError(err.response?.data?.error?.message || 'Failed to load attribution models') })
    }
    return () => { active = false }
  }, [currentClinicId, supported, startDate, endDate, scope, lookbackDays, attributionWindow, accountingMode, refresh, retry])

  if (!supported) return <p className="text-muted-foreground">Select a clinic to compare attribution models.</p>

  const displayed = result && view === 'channels' ? { ...result, models: result.models.filter(model => model.id === modelId) } : result
  const missingWindowDays = missingDaysNote(result?.spend_coverage?.missing_days)
  const controlClass = 'h-9 rounded-md border border-input bg-background px-3 text-sm'

  return (
    <Card>
      <CardHeader className="space-y-4">
        <div>
          <CardTitle>{view === 'channels' ? 'Channel attribution' : 'Same sales. Different credit.'}</CardTitle>
          <p className="mt-2 text-sm text-muted-foreground max-w-3xl">
            Compare how recorded visits and price-book closing touches share Science Co’s paid orders and revenue.
            Actual sales use payment dates in Eastern time. Choose when to report their attribution credit below.
          </p>
        </div>
        <div className="flex flex-wrap gap-4">
          {view === 'channels' && <label className="flex flex-col gap-1 text-xs text-muted-foreground">Attribution model
            <select className={controlClass} value={modelId} disabled={!result} onChange={event => setModelId(event.target.value)}>
              {result ? result.models.map(model => <option key={model.id} value={model.id}>{model.label}</option>) : <option value={modelId}>Loading models…</option>}
            </select>
          </label>}
          <AttributionWindowControl value={attributionWindow} onChange={setAttributionWindow} />
          <label className="flex flex-col gap-1 text-xs text-muted-foreground">Accounting mode
            <select className={controlClass} value={accountingMode} onChange={event => setAccountingMode(event.target.value)}>
              <option value="payment">Event date</option><option value="touchpoint">Click date</option>
            </select>
          </label>
          <label className="flex flex-col gap-1 text-xs text-muted-foreground">
            Subscription filter
            <select className={controlClass} value={scope} onChange={event => setScope(event.target.value)}>
              <option value="new">New orders</option>
              <option value="all">All paid orders</option>
            </select>
          </label>
          {view !== 'channels' && <label className="flex flex-col gap-1 text-xs text-muted-foreground">
            Compare
            <select className={controlClass} value={metric} onChange={event => setMetric(event.target.value)}>
              <option value="revenue">Attributed revenue</option>
              <option value="orders">Order credit</option>
            </select>
          </label>}
        </div>
      </CardHeader>
      <CardContent className="space-y-6">
        <AttributionWindowExplanation />
        {error ? (
          <div role="alert" className="flex items-center gap-3 text-sm text-destructive">
            {error}<Button variant="outline" size="sm" onClick={() => setRetry(value => value + 1)}>Retry</Button>
          </div>
        ) : !result ? (
          <div role="status" className="flex items-center gap-2 py-12 text-muted-foreground"><Loader2 className="h-4 w-4 animate-spin" />Loading model comparison…</div>
        ) : (
          <>
            <div className="flex flex-wrap gap-x-8 gap-y-2 text-sm">
              <span><strong>{count(result.totals.orders)}</strong> paid orders</span>
              <span><strong>{formatMoney(result.totals.revenue)}</strong> revenue</span>
              <span><strong>{count(result.totals.orders_with_journeys)} / {count(result.totals.orders)}</strong> orders with eligible visits</span>
              <span><strong>{count(result.totals.price_book_orders)}</strong> orders with price-book closing evidence</span>
            </div>
            {accountingMode === 'touchpoint' && <p className="text-sm text-muted-foreground">{result.maturity.complete ? 'Selected touch dates have completed their attribution window.' : `Recent touch dates are still maturing: allow up to ${result.maturity.days} days for payments to arrive.`} Credit can differ from actual payments in these dates. Payments observed through {new Date(result.observedAt).toLocaleString()}.</p>}
            {displayed.models.every(model => !model.rows.length) ? (
              <p className="py-8 text-sm text-muted-foreground">No attribution credit in this selection. Try a wider date range or all paid orders.</p>
            ) : (
              view === 'channels'
                ? <AttributionChannelTable comparison={displayed} scope={scope} lookbackDays={lookbackDays} />
                : <AttributionCreditTable comparison={displayed} metric={metric} scope={scope} lookbackDays={lookbackDays}
                    onLoadCampaigns={channel => dailyReportAPI.getCampaignAttribution({ channel, startDate, endDate, scope, lookbackDays, attributionWindow, accountingMode })
                      .then(response => response.data.data)} />
            )}
            <section className="space-y-3" aria-label="Blended model efficiency">
              <h3 className="font-medium">Blended model efficiency</h3>
              <p className="text-sm text-muted-foreground">{result.spend == null ? 'Unavailable: no ad spend is connected for this selection.' : `${formatMoney(result.spend)} combined ad spend in the selected dates.`} Return divides credited revenue by combined spend. Cost divides combined spend by credited new orders. These include organic and team credit, exclude unattributed and outside-window credit, and do not measure channel ROAS or incremental lift.</p>
              {/* A partial window is summed, not withheld — so the dates it is
                  short of are named, or the figure reads as a cheaper period. */}
              {missingWindowDays && result.spend != null ? <p className="text-sm text-amber-600 dark:text-amber-400">{missingWindowDays} Combined spend is the sum of the remaining {result.spend_coverage.days - result.spend_coverage.missing_days.length} of {result.spend_coverage.days} dates.</p> : null}
              {result.spend != null && <div className="overflow-x-auto rounded-md border"><table className="w-full text-sm tabular-nums">
                <thead><tr><th className="p-3 text-left">Model</th><th className="p-3 text-right">Blended attributed ROAS</th><th className="p-3 text-right">Cost per credited new order</th></tr></thead>
                <tbody>{displayed.models.map(model => <tr key={model.id} className="border-t"><th className="p-3 text-left font-normal">{model.label}{model.reconciles === false ? ' (overlapping)' : ''}</th><td className="p-3 text-right">{model.efficiency.roas == null ? '—' : `${model.efficiency.roas.toFixed(2)}×`}</td><td className="p-3 text-right">{model.efficiency.cost_per_new_order == null ? '—' : formatMoney(model.efficiency.cost_per_new_order)}</td></tr>)}</tbody>
              </table></div>}
            </section>
            <p className="text-sm text-muted-foreground">Multi / Triple Attribution gives full credit independently to each channel. Its total is overlapping channel credit and can exceed actual sales. Use the paid-order and revenue figures above for actual totals.</p>
            <div className="rounded-md border bg-muted/30 p-4 text-sm space-y-2">
              <p>Sales Team and Reddit price books identify the closing touch on new sales. A matching recorded visit is reused; otherwise a price-book touch is added at payment time. First Touch preserves the recorded sequence; Last Touch credits the price-book closer.</p>
              <p className="text-muted-foreground">{count(result.totals.bigquery_touches_added || 0)} orders include a paid click from BigQuery that core did not record{result.bigquery_touches && !result.bigquery_touches.checked ? ` (BigQuery clicks were not added: ${result.bigquery_touches.reason})` : ''}. {count(result.totals.price_book_touches_added)} orders include an added price-book touch, not a tracked visit. Undated visits are excluded; orders without eligible visits or closing evidence stay unattributed. Renewals and unclassified order types are included only under “All paid orders” and receive no added price-book touch. Price books reflect the patient’s current record, so later changes can restate historical attribution.</p>
              <p className="text-muted-foreground">Clicks-Only leaves {count(result.totals.unclassified_funnel_orders)} orders unattributed because their journeys contain channels without a funnel classification, including Google traffic without a verified brand/non-brand split. Sales Team counts as lower funnel; Reddit counts as upper funnel, including price-book touches. This is a local interpretation, not Northbeam’s proprietary output.</p>
            </div>
            <section className="space-y-3" aria-label="Model explanations">
              <h3 className="font-medium">Explore each model</h3>
              <p className="text-sm text-muted-foreground">Open a panel for credit rules, a $90 sale example, and price-book behavior.</p>
              <div className="flex flex-wrap gap-2">
                {result.models.map(model => <AttributionModelDetails key={model.id} model={model} lookbackDays={lookbackDays} scope={scope} attributionWindow={attributionWindow} accountingMode={accountingMode} />)}
              </div>
            </section>
            <div className="grid gap-4 sm:grid-cols-2">
              {result.unavailable.map(model => (
                <div key={model.label} className="border-t pt-4">
                  <AttributionModelDetails model={model} lookbackDays={lookbackDays} scope={scope} attributionWindow={attributionWindow} accountingMode={accountingMode} />
                  <p className="mt-2 text-xs font-medium text-muted-foreground">Unavailable</p>
                  <p className="mt-1 text-sm text-muted-foreground">{model.reason}</p>
                </div>
              ))}
            </div>
          </>
        )}
      </CardContent>
    </Card>
  )
}
